import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import CabeceraPagina from "../../components/CabeceraPagina.jsx";
import { EstadoVacio, euros } from "../../components/ui.jsx";
import { ESTADOS_OT, tonoEstado } from "./datos.js";
import RecepcionRapida from "./RecepcionRapida.jsx";
import FormOrden from "./FormOrden.jsx";
import MenuImprimirOrden from "../../components/MenuImprimirOrden.jsx";
import ModalRecepcion from "./ModalRecepcion.jsx";
import FinalizarOrdenModal from "./FinalizarOrdenModal.jsx";
import { urlApiConSesion } from "../../lib/sesion.js";

const fechaHora = (iso) =>
  iso
    ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

// Aviso de entrega en las tarjetas y la lista: hora de finalización y de
// entrega (cada una con la del momento en que se picó) y si el cliente ya
// está avisado para la recogida.
function BadgeEntrega({ orden }) {
  if (!orden.entrega) return null;
  const { fecha: fFin, fechaEntrega: fEnt, clienteAvisado, avisoMedio, fotos, notas } = orden.entrega;
  const medios = { telefono: "teléfono", whatsapp: "WhatsApp", sms: "SMS", email: "email", en_persona: "en persona" };
  const esEntregado = orden.estado === "entregado";
  return (
    <p className="mt-1 flex items-center flex-wrap gap-x-1.5 gap-y-0.5 text-[0.6875rem]">
      {fFin && (
        <span className="text-slate-500 num" title="Fecha y hora de finalización del trabajo">
          Finalizado: {fechaHora(fFin)}
        </span>
      )}
      {esEntregado && (fEnt || fFin) && (
        <span className="text-slate-500 num" title="Fecha y hora de la entrega al cliente">
          Entregado: {fechaHora(fEnt ?? fFin)}
        </span>
      )}
      {fotos?.length > 0 && (
        <span className="text-slate-400" title={`${fotos.length} foto(s) del vehículo terminado`}>
          📷 {fotos.length}
        </span>
      )}
      {notas && (
        <span
          title={`Tareas pendientes: ${notas}`}
          className="rounded-full bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-px font-semibold"
        >
          Pendiente
        </span>
      )}
      {clienteAvisado ? (
        <span
          className="rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 px-1.5 py-px font-semibold"
          title={`Cliente avisado${avisoMedio ? ` por ${medios[avisoMedio] ?? avisoMedio}` : ""}`}
        >
          Avisado
        </span>
      ) : (
        <span className="rounded-full bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-px font-semibold" title="Aún no se ha avisado al cliente">
          Sin avisar
        </span>
      )}
    </p>
  );
}

// Icono de cámara para la recepción digital (fotos del estado + firma).
function IconCamara() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

// Campanita: entrega y aviso al cliente (órdenes finalizadas/entregadas).
function IconCampana() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString("es-ES") : "—");

// Nº del presupuesto vinculado a la orden (píldora violeta).
function BadgePresupuesto({ numero }) {
  if (!numero) return null;
  return (
    <span
      title={`Nace del presupuesto ${numero}`}
      className="inline-block rounded-full border bg-violet-100 text-violet-700 border-violet-200 text-[0.625rem] font-semibold px-1.5 py-px ml-1.5 align-middle"
    >
      {numero}
    </span>
  );
}

// Desplegable de estado con aspecto de pill coloreada (referencia).
const CLASES_PILL_ESTADO = {
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  cyan: "bg-sky-100 text-sky-700 border-sky-200",
  green: "bg-emerald-100 text-emerald-700 border-emerald-200",
  slate: "bg-slate-100 text-slate-600 border-slate-200",
};

// Vista kanban (referencia RO App /orders/board): una columna por estado,
// tarjetas arrastrables que cambian el estado de la orden al soltarse.
function TableroKanban({ ordenes, onMover, onEditar, onFacturar, onRecepcion, onEntrega, onBorrar }) {
  // Auto-scroll de la ventana mientras se arrastra: sin esto, las tarjetas
  // que están abajo del todo no se pueden llevar a las columnas de arriba
  // (el navegador no desplaza la página al arrastrar en todos los casos).
  useEffect(() => {
    const MARGEN = 90;
    const VELOCIDAD = 20;
    let raf = null;
    let dy = 0;
    const paso = () => {
      window.scrollBy(0, dy);
      raf = dy !== 0 ? requestAnimationFrame(paso) : null;
    };
    const alArrastrar = (e) => {
      if (e.clientY > 0 && e.clientY < MARGEN) dy = -VELOCIDAD;
      else if (e.clientY > window.innerHeight - MARGEN) dy = VELOCIDAD;
      else dy = 0;
      if (dy !== 0 && raf === null) raf = requestAnimationFrame(paso);
    };
    const alSoltar = () => { dy = 0; };
    window.addEventListener("dragover", alArrastrar);
    window.addEventListener("drop", alSoltar);
    window.addEventListener("dragend", alSoltar);
    return () => {
      window.removeEventListener("dragover", alArrastrar);
      window.removeEventListener("drop", alSoltar);
      window.removeEventListener("dragend", alSoltar);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 items-start">
      {ESTADOS_OT.map((col) => {
        const enColumna = ordenes
          .filter((o) => o.estado === col.clave)
          .sort((a, b) =>
            new Date(b.estadoActualizadoEn ?? b.updatedAt ?? b.createdAt ?? 0) -
            new Date(a.estadoActualizadoEn ?? a.updatedAt ?? a.createdAt ?? 0)
          );
        return (
          <div
            key={col.clave}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const id = e.dataTransfer.getData("text/orden-id");
              const o = ordenes.find((x) => x._id === id);
              if (o && o.estado !== col.clave) onMover(o, col.clave);
            }}
            className="rounded-xl bg-slate-100/80 border border-slate-200/70 p-2.5 min-h-[220px]"
          >
            <div className="flex items-center justify-between px-1 pb-2.5">
              <span
                className={`inline-block rounded-full border text-[0.6875rem] font-semibold px-3 py-1 ${
                  CLASES_PILL_ESTADO[col.tono] ?? CLASES_PILL_ESTADO.slate
                }`}
              >
                {col.nombre}
              </span>
              <span className="text-xs font-medium text-slate-400 num">{enColumna.length}</span>
            </div>
            <div className="space-y-2">
              {enColumna.map((o) => (
                <div
                  key={o._id}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/orden-id", o._id)}
                  className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 cursor-grab active:cursor-grabbing hover:border-accent/60 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold text-slate-800 num">
                      {o.numero}
                      <BadgePresupuesto numero={o.presupuestoNumero} />
                    </p>
                    <p className="num text-sm font-semibold text-slate-700">{euros(o.total)}</p>
                  </div>
                  <p className="num text-sm text-slate-600 mt-0.5">{o.matricula}</p>
                  {o.vehiculo?.modelo && (
                    <p className="text-xs text-slate-500 truncate" title="Modelo del vehículo">
                      {o.vehiculo.marca ? `${o.vehiculo.marca} ` : ""}{o.vehiculo.modelo}
                    </p>
                  )}
                  <p className="text-xs text-slate-500 truncate">{o.clienteNombre ?? "—"}</p>
                  {o.trabajos?.length > 0 && (
                    <p className="text-[0.6875rem] text-slate-400 mt-1 truncate">{o.trabajos.join(", ")}</p>
                  )}
                  <BadgeEntrega orden={o} />
                  {/* Cambio de estado sin arrastrar: imprescindible cuando la
                      tarjeta está abajo del todo en columnas largas */}
                  <select
                    value={o.estado}
                    onChange={(e) => e.target.value !== o.estado && onMover(o, e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    draggable={false}
                    className={`mt-2 w-full rounded-full border text-[0.6875rem] font-semibold px-2 py-1 cursor-pointer ${
                      CLASES_PILL_ESTADO[tonoEstado(o.estado)] ?? CLASES_PILL_ESTADO.slate
                    }`}
                    title="Cambiar de estado sin arrastrar"
                  >
                    {ESTADOS_OT.map((col) => (
                      <option key={col.clave} value={col.clave}>{col.nombre}</option>
                    ))}
                  </select>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                    <span className="text-[0.6875rem] text-slate-400 num">{fecha(o.fechaEntrada)}</span>
                    <span className="flex items-center gap-2">
                      <MenuImprimirOrden orden={o} pequeno />
                      {["finalizado", "entregado"].includes(o.estado) && (
                        <button
                          onClick={() => onEntrega(o)}
                          title={
                            o.entrega?.clienteAvisado
                              ? "Cliente avisado — abrir entrega y aviso"
                              : "Entrega y aviso al cliente (fecha, fotos, avisar para la recogida)"
                          }
                          className={`inline-flex items-center transition-colors ${
                            o.entrega?.clienteAvisado
                              ? "text-emerald-500 hover:text-emerald-600"
                              : "text-amber-500 hover:text-amber-600"
                          }`}
                        >
                          <IconCampana />
                        </button>
                      )}
                      <button
                        onClick={() => onRecepcion(o)}
                        title="Recepción digital (fotos + firma)"
                        className={`inline-flex items-center transition-colors ${
                          o.recepcionDigital?.firma?.fecha
                            ? "text-emerald-500 hover:text-emerald-600"
                            : "text-slate-400 hover:text-accent"
                        }`}
                      >
                        <IconCamara />
                      </button>
                      <button
                        onClick={() => onEditar(o)}
                        className="text-[0.6875rem] text-accent hover:underline"
                      >
                        Editar
                      </button>
                      {!o.factura && ["finalizado", "entregado"].includes(o.estado) && (
                        <button
                          onClick={() => onFacturar(o)}
                          className="text-[0.6875rem] text-amber-600 hover:underline"
                        >
                          Facturar
                        </button>
                      )}
                      <button
                        onClick={() => onBorrar(o)}
                        title="Borrar orden"
                        className="text-[0.6875rem] text-rose-400 hover:text-rose-600 hover:underline"
                      >
                        Borrar
                      </button>
                    </span>
                  </div>
                  <AdjuntoValoracion orden={o} />
                </div>
              ))}
              {enColumna.length === 0 && (
                <p className="text-[0.6875rem] text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-lg">
                  Arrastra aquí
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AdjuntoValoracion({ orden }) {
  const [subiendo, setSubiendo] = useState(false);
  async function subir(e) {
    const archivo = e.target.files?.[0];
    e.target.value = "";
    if (!archivo) return;
    setSubiendo(true);
    const form = new FormData();
    form.append("archivo", archivo);
    const r = await fetch(`/api/taller/ordenes/${orden._id}/valoracion-pdf`, { method: "POST", body: form });
    setSubiendo(false);
    if (!r.ok) alert((await r.json()).error || "No se pudo adjuntar el PDF");
    else window.location.reload();
  }
  return (
    <div className="mt-2 flex items-center gap-2 text-[0.6875rem]">
      {orden.valoracionPdf?.ruta ? (
        <a href={urlApiConSesion(orden.valoracionPdf.ruta)} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline truncate" title={orden.valoracionPdf.nombre}>
          PDF valoración
        </a>
      ) : (
        <span className="text-slate-400">Sin valoración PDF</span>
      )}
      <label className="text-accent hover:underline cursor-pointer ml-auto">
        {subiendo ? "Subiendo…" : orden.valoracionPdf ? "Cambiar PDF" : "Adjuntar PDF"}
        <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={subir} disabled={subiendo} />
      </label>
    </div>
  );
}

export default function TallerOrdenesPage() {
  const [lista, setLista] = useState(null);
  const [error, setError] = useState(null);
  const [recepcion, setRecepcion] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  // undefined = cerrado · null = orden nueva · objeto = edición
  const [ordenForm, setOrdenForm] = useState(undefined);
  const [recepcionOT, setRecepcionOT] = useState(undefined); // orden en recepción digital
  const [finalizando, setFinalizando] = useState(undefined); // orden en el modal de finalizar
  const [recepcionNueva, setRecepcionNueva] = useState(false); // recién creada (flujo recepción rápida)
  const [vista, setVista] = useState("tablero"); // "tablero" (kanban) | "lista"
  const [verArchivadas, setVerArchivadas] = useState(false); // entregadas de más de 7 días
  const [params, setParams] = useSearchParams();

  // Abre directamente una orden cuando llega ?abrir=<id> (p. ej. desde el historial del vehículo).
  useEffect(() => {
    const id = params.get("abrir");
    if (!id || !lista) return;
    const o = lista.find((x) => x._id === id);
    if (o) {
      setOrdenForm(o);
      params.delete("abrir");
      setParams(params, { replace: true });
    }
  }, [lista, params, setParams]);

  async function cargar() {
    try {
      // ?tablero=1: el servidor oculta las entregadas de más de 7 días (se
      // quedan solo en el historial del vehículo) para que el tablero no se
      // llene de coches ya entregados (propuesta 15/10).
      const r = await fetch(`/api/taller/ordenes${verArchivadas ? "" : "?tablero=1"}`);
      const datos = await r.json();
      if (!r.ok) throw new Error(datos.error || "Error al cargar");
      setLista(datos);
    } catch (e) {
      setError(e.message);
      setLista([]);
    }
  }

  useEffect(() => {
    cargar();
  }, [verArchivadas]);

  async function cambiarEstado(o, estado) {
    // Al finalizar (trabajo terminado) o entregar (coche al cliente) se
    // abre la ventana de entrega con la fecha correspondiente.
    if (estado === "finalizado" || estado === "entregado") {
      setFinalizando({ orden: o, destino: estado });
      return;
    }
    const r = await fetch(`/api/taller/ordenes/${o._id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ estado }),
    });
    const datos = await r.json();
    if (r.ok) cargar();
    else alert(datos.error || "No se pudo cambiar el estado");
  }

  async function borrar(o) {
    const texto = o.factura
      ? `La orden ${o.numero} tiene la factura ${o.numeroFactura ?? "asociada"}.\n\n` +
        `La factura NO se borra: es un documento legal y queda registrada (se reimprime igual, con los datos de la reparación).\n` +
        `Se borra solo la orden: sus fotos, el historial del vehículo y los tiempos de los operarios.\n\n` +
        `¿Borrar la orden?`
      : `¿Borrar la orden ${o.numero}?\nSe borran también sus fotos, el historial del vehículo y los tiempos de los operarios.`;
    if (!window.confirm(texto)) return;
    const r = await fetch(`/api/taller/ordenes/${o._id}`, { method: "DELETE" });
    if (r.ok) cargar();
    else alert((await r.json()).error || "No se pudo borrar");
  }

  async function facturar(o) {
    const aseg = o.facturarA === "aseguradora" && o.aseguradora?.nombre;
    const texto = aseg
      ? `¿Generar la factura (borrador) de ${o.numero} a nombre de ${o.aseguradora.nombre}, con sus descuentos negociados aplicados?`
      : `¿Generar la factura (borrador) de ${o.numero} por ${euros(o.total)}?`;
    if (!window.confirm(texto)) return;
    const r = await fetch(`/api/taller/ordenes/${o._id}/facturar`, { method: "POST" });
    const datos = await r.json();
    if (r.ok) {
      cargar();
      alert("Factura creada en Ventas como borrador: emítela desde allí para enviarla a VeriFactu.");
    } else alert(datos.error || "No se pudo facturar");
  }

  const q = busqueda.trim().toLowerCase();
  const filtrada = (lista ?? []).filter(
    (o) =>
      !q ||
      o.numero.toLowerCase().includes(q) ||
      o.matricula.toLowerCase().includes(q) ||
      (o.clienteNombre ?? "").toLowerCase().includes(q) ||
      (o.cliente?.telefono ?? "").includes(q) ||
      (o.telefono ?? "").includes(q) ||
      (o.aseguradora?.nombre ?? o.aseguradoraNombre ?? "").toLowerCase().includes(q) ||
      (o.numeroSiniestro ?? "").toLowerCase().includes(q) ||
      (o.presupuestoNumero ?? "").toLowerCase().includes(q) ||
      (o.estado ?? "").replace(/_/g, " ").includes(q)
  );

  return (
    <>
      <CabeceraPagina titulo="Órdenes de trabajo" descripcion="Chapa, pintura y mecánica.">
        <div className="inline-flex rounded-lg overflow-hidden border border-slate-300 bg-white text-[0.78125rem] font-semibold mr-2">
          <button
            onClick={() => setVista("tablero")}
            className={`px-3.5 py-2 transition-colors ${vista === "tablero" ? "seg-activo" : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"}`}
          >
            Tablero
          </button>
          <button
            onClick={() => setVista("lista")}
            className={`px-3.5 py-2 transition-colors ${vista === "lista" ? "seg-activo" : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"}`}
          >
            Lista
          </button>
        </div>
        <button onClick={() => setOrdenForm(null)} className="btn-primary mr-2">
          + Nueva orden
        </button>
        <button
          onClick={() => setRecepcion(true)}
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors shadow-sm"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />
          </svg>
          Recepción rápida
        </button>
      </CabeceraPagina>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          className="input w-full max-w-sm"
          placeholder="Buscar por nº, matrícula, cliente, teléfono, compañía o siniestro…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <label
          className="inline-flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none"
          title="Las órdenes entregadas se archivan solas a los 7 días y quedan en el historial del vehículo"
        >
          <input
            type="checkbox"
            className="h-4 w-4 accent-emerald-500"
            checked={verArchivadas}
            onChange={(e) => setVerArchivadas(e.target.checked)}
          />
          Ver archivadas (entregadas hace más de 7 días)
        </label>
      </div>

      {vista === "tablero" ? (
        lista && (
          <TableroKanban
            ordenes={filtrada}
            onMover={cambiarEstado}
            onEditar={setOrdenForm}
            onFacturar={facturar}
            onRecepcion={setRecepcionOT}
            onEntrega={(o) => setFinalizando({ orden: o, destino: o.estado })}
            onBorrar={borrar}
          />
        )
      ) : (
      <div className="panel px-3.5 py-2">
        {!lista ? null : filtrada.length === 0 ? (
          <EstadoVacio
            titulo={q ? "Sin resultados" : "Sin órdenes de trabajo"}
            descripcion="La recepción rápida crea la orden y da de alta el vehículo en un paso."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Nº</th>
                  <th>Vehículo</th>
                  <th>Cliente</th>
                  <th>Trabajos</th>
                  <th>Entrada</th>
                  <th>Estado</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtrada.map((o) => (
                  <tr key={o._id}>
                    <td className="font-bold text-white whitespace-nowrap num">
                      {o.numero}
                      <BadgePresupuesto numero={o.presupuestoNumero} />
                    </td>
                    <td className="text-slate-300 num">
                      {o.matricula}
                      {o.vehiculo?.modelo && <p className="text-[0.6875rem] text-slate-500">{o.vehiculo.marca ? `${o.vehiculo.marca} ` : ""}{o.vehiculo.modelo}</p>}
                    </td>
                    <td className="text-slate-300">
                      {o.clienteNombre ?? "—"}
                      {o.aseguradora?.nombre && (
                        <p className="text-[0.6875rem] text-amber-300/90 truncate max-w-[160px]" title={`Siniestro${o.numeroSiniestro ? ` ${o.numeroSiniestro}` : ""}`}>
                          {o.facturarA === "aseguradora" ? "→ " : ""}{o.aseguradora.nombre}
                        </p>
                      )}
                    </td>
                    <td className="text-slate-400">{o.trabajos?.length > 0 ? o.trabajos.join(", ") : "—"}</td>
                    <td className="text-slate-400 whitespace-nowrap num">
                      {fecha(o.fechaEntrada)}
                      <BadgeEntrega orden={o} />
                    </td>
                    <td>
                      <select
                        value={o.estado}
                        onChange={(e) => cambiarEstado(o, e.target.value)}
                        className={`cursor-pointer appearance-none rounded-full border text-[0.6875rem] font-semibold pl-3 pr-7 py-1 bg-no-repeat focus:outline-none ${
                          CLASES_PILL_ESTADO[tonoEstado(o.estado)] ?? CLASES_PILL_ESTADO.slate
                        }`}
                        style={{
                          backgroundImage:
                            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
                          backgroundPosition: "right 9px center",
                        }}
                      >
                        {ESTADOS_OT.map((e2) => (
                          <option key={e2.clave} value={e2.clave}>{e2.nombre}</option>
                        ))}
                      </select>
                    </td>
                    <td className="text-right text-slate-300 whitespace-nowrap num">{euros(o.total)}</td>
                    <td className="text-right whitespace-nowrap">
                      <MenuImprimirOrden orden={o} />
                      {["finalizado", "entregado"].includes(o.estado) && (
                        <button
                          onClick={() => setFinalizando({ orden: o, destino: o.estado })}
                          title={
                            o.entrega?.clienteAvisado
                              ? "Cliente avisado — abrir entrega y aviso"
                              : "Entrega y aviso al cliente (fecha, fotos, avisar para la recogida)"
                          }
                          className={`inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors align-middle mx-1 ${
                            o.entrega?.clienteAvisado
                              ? "text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10"
                              : "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                          }`}
                        >
                          <IconCampana />
                        </button>
                      )}
                      <button
                        onClick={() => setRecepcionOT(o)}
                        title="Recepción digital (fotos + firma)"
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors align-middle mx-1 ${
                          o.recepcionDigital?.firma?.fecha
                            ? "text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10"
                            : "text-slate-400 hover:text-accent hover:bg-accent/10"
                        }`}
                      >
                        <IconCamara />
                      </button>
                      <button onClick={() => setOrdenForm(o)} className="text-xs text-accent hover:underline mr-3 ml-1">
                        Editar
                      </button>
                      {o.factura ? (
                        <Link to="/ventas" className="text-xs text-emerald-300 hover:underline mr-3">
                          Facturada
                        </Link>
                      ) : (
                        ["finalizado", "entregado"].includes(o.estado) && (
                          <button onClick={() => facturar(o)} className="text-xs text-amber-300 hover:underline mr-3">
                            Facturar
                          </button>
                        )
                      )}
                      <button onClick={() => borrar(o)} className="text-xs text-rose-400 hover:underline">
                        Borrar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {recepcion && (
        <RecepcionRapida
          onCerrar={() => setRecepcion(false)}
          onCreada={(datos) => {
            setRecepcion(false);
            cargar();
            // Al terminar el alta se abre la recepción digital de la nueva OT
            // para fotos del estado, firma del cliente e impresión de la hoja.
            if (datos?.orden) {
              setRecepcionNueva(true);
              setRecepcionOT(datos.orden);
            }
          }}
        />
      )}

      {ordenForm !== undefined && (
        <FormOrden
          orden={ordenForm}
          onCerrar={() => setOrdenForm(undefined)}
          onGuardada={() => {
            setOrdenForm(undefined);
            cargar();
          }}
        />
      )}

      {recepcionOT !== undefined && (
        <ModalRecepcion
          orden={recepcionOT}
          recienCreada={recepcionNueva}
          onCerrar={() => {
            setRecepcionOT(undefined);
            setRecepcionNueva(false);
          }}
          onGuardada={cargar}
        />
      )}

      {finalizando !== undefined && (
        <FinalizarOrdenModal
          orden={finalizando.orden}
          destino={finalizando.destino}
          onCerrar={() => setFinalizando(undefined)}
          onFinalizada={() => {
            setFinalizando(undefined);
            cargar();
          }}
        />
      )}
    </>
  );
}
